// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom"; // TODO: useRouter → useNavigate (nav(x) → nav(x), router.replace(x) → nav(x, {replace:true}))
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  Button,
  ConfirmDialog,
  PostingStateBadge,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatVnd,
  useToast,
} from "@chotca/ui";
import {
  BENEFIT_LABEL_VI,
  EMPLOYMENT_TYPE_LABEL_VI,
  GENDER_LABEL_VI,
  POSTING_STATE_LABEL_VI,
  SALARY_TYPE_LABEL_VI,
  SKILL_LABEL_VI,
  VENUE_TYPE_LABEL_VI,
  WORKER_POSITION_LABEL_VI,
  formatSalaryRange,
  type EmploymentType,
  type Gender,
  type PostingState,
  type SalaryType,
  type VenueType,
  type WorkerPosition,
} from "@chotca/config";
import { useAdminApplicants } from "@chotca/api-client";
import { getSdk } from "@chotca/api-client";
import { qk } from "@chotca/api-client";
import { AdminApplicantRow } from "@/components/admin-applicant-row";
import { useTabParam } from "@/lib/use-tab-param";
import { WEB_APEX_URL } from "@/env";

export function PostingDetailClient({ postingId }: { postingId: string }) {
  const t = useTranslations("admin");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [confirm, setConfirm] = React.useState<
    "close" | "cancel" | "delete" | null
  >(null);
  const [tab, setTab] = useTabParam("applicants");

  const postingQ = useQuery({
    queryKey: qk.admin.posting(postingId),
    queryFn: async () =>
      getSdk()
        .admin.getPosting(postingId)
        .catch(() => null),
  });

  const close = useMutation({
    mutationFn: () => getSdk().admin.closePosting(postingId),
    onSuccess: () => {
      toast({ tone: "success", title: "Đã tạm dừng tin" });
      qc.invalidateQueries({ queryKey: qk.admin.posting(postingId) });
      setConfirm(null);
    },
  });
  const cancel = useMutation({
    mutationFn: () => getSdk().admin.cancelPosting(postingId),
    onSuccess: () => {
      toast({ tone: "success", title: "Đã huỷ tin" });
      qc.invalidateQueries({ queryKey: qk.admin.posting(postingId) });
      setConfirm(null);
    },
    onError: () => toast({ tone: "error", title: "Không thể huỷ" }),
  });
  const remove = useMutation({
    mutationFn: () => getSdk().admin.deletePosting(postingId),
    onSuccess: () => {
      toast({ tone: "success", title: "Đã xoá" });
      nav("/postings");
    },
    onError: () => toast({ tone: "error", title: "Không thể xoá" }),
  });

  if (postingQ.isLoading) return <Skeleton className="h-96 rounded-2xl" />;
  const p = postingQ.data;
  if (!p)
    return (
      <p className="text-center text-sm text-ash">Không tìm thấy tin này.</p>
    );

  const positionLabel =
    WORKER_POSITION_LABEL_VI[p.position as WorkerPosition] ?? p.position;
  const empLabel =
    EMPLOYMENT_TYPE_LABEL_VI[p.employmentType as EmploymentType] ??
    p.employmentType;
  const venueLabel =
    VENUE_TYPE_LABEL_VI[p.venueType as VenueType] ?? p.venueType;
  const salary = formatSalaryRange(
    p.salaryMin as unknown as number | bigint | string,
    p.salaryMax as unknown as number | bigint | string,
    SALARY_TYPE_LABEL_VI[p.salaryType as SalaryType],
  );
  const isOpen = p.state === "OPEN";

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <Link to="/postings" className="text-xs text-ash hover:text-ink">
          ← {t("postingsTitle")}
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink/60">
              <span>{positionLabel}</span>
              <span>·</span>
              <span>{empLabel}</span>
              <span>·</span>
              <span>{venueLabel}</span>
            </div>
            <h1 className="mt-1 flex flex-wrap items-center gap-2 text-2xl font-bold text-ink">
              {p.title} <PostingStateBadge state={p.state as PostingState} />
            </h1>
            <p className="mt-1 text-sm text-ash">
              <Link
                to={`/orgs/${p.org.id}`}
                className="underline hover:text-ink"
              >
                {p.org.name}
              </Link>{" "}
              · {p.org.district}
            </p>
            {p.sourceUrl ? (
              <a
                href={p.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-xs text-lime-dark underline hover:text-ink"
              >
                Xem bài gốc trên Facebook ↗
              </a>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">
                {salary}
              </span>
              <span className="rounded-full bg-ink/5 px-2 py-0.5">
                Tuyển {p.headcount} người
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* "Đăng lại" works for any state — re-upping an expired/closed tin
                is the main reason to copy. Prefills the create form (org +
                fields) so admin only reviews + tweaks. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav(`/postings/new?duplicate=${postingId}`)}
            >
              Đăng lại
            </Button>
            <Button
              size="sm"
              onClick={() => nav(`/postings/${postingId}/edit`)}
            >
              Sửa
            </Button>
            <MoreMenu
              items={
                [
                  isOpen
                    ? {
                        label: "Sao chép link",
                        onClick: async () => {
                          const url = `${WEB_APEX_URL}/postings/${postingId}`;
                          try {
                            await navigator.clipboard.writeText(url);
                            toast({
                              tone: "success",
                              title: "Đã sao chép link",
                            });
                          } catch {
                            window.prompt("Sao chép link:", url);
                          }
                        },
                      }
                    : null,
                  isOpen
                    ? {
                        label: "Tạm dừng",
                        onClick: () => setConfirm("close"),
                      }
                    : null,
                  isOpen
                    ? {
                        label: "Huỷ tin",
                        onClick: () => setConfirm("cancel"),
                        destructive: true,
                      }
                    : null,
                  {
                    label: "Xoá tin",
                    onClick: () => setConfirm("delete"),
                    destructive: true,
                  },
                ].filter(Boolean) as MoreMenuItem[]
              }
            />
          </div>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="applicants">{t("tabApplicants")}</TabsTrigger>
          <TabsTrigger value="info">{t("tabInfo")}</TabsTrigger>
        </TabsList>
        <TabsContent value="applicants">
          <ApplicantsTab
            postingId={postingId}
            orgId={p.org.id}
            postingActive={isOpen}
          />
        </TabsContent>
        <TabsContent value="info">
          <PostingInfo posting={p} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirm === "close"}
        onClose={() => setConfirm(null)}
        title="Tạm dừng tin?"
        description="Tin sẽ không còn nhận ứng viên mới."
        confirmLabel="Tạm dừng"
        loading={close.isPending}
        onConfirm={() => close.mutate()}
      />
      <ConfirmDialog
        open={confirm === "cancel"}
        onClose={() => setConfirm(null)}
        title="Huỷ tin?"
        description="Toàn bộ ứng viên đang APPLIED sẽ chuyển sang REJECTED."
        confirmLabel="Huỷ tin"
        destructive
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
      />
      <ConfirmDialog
        open={confirm === "delete"}
        onClose={() => setConfirm(null)}
        title="Xoá tin?"
        description="Xoá hẳn tin cùng toàn bộ hồ sơ ứng tuyển, không thể khôi phục. Lượt phỏng vấn đang giữ sẽ được hoàn lại cho nhà tuyển dụng."
        confirmLabel="Xoá"
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}

type Posting = NonNullable<
  Awaited<ReturnType<ReturnType<typeof getSdk>["admin"]["getPosting"]>>
>;

function PostingInfo({ posting }: { posting: Posting }) {
  const reqs = posting.requirements;
  const showReqs =
    reqs &&
    (reqs.gender ||
      reqs.minAge ||
      reqs.maxAge ||
      reqs.minHeightCm ||
      reqs.motorbikeRequired ||
      reqs.excludeVisibleTattoo ||
      reqs.kycCccdRequired);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-smoke bg-paper p-5">
        <h3 className="mb-2 text-sm font-semibold text-ink">Mô tả công việc</h3>
        <p className="whitespace-pre-wrap text-sm text-ink/80">
          {posting.description}
        </p>
        {posting.softNotes ? (
          <p className="mt-3 whitespace-pre-wrap text-sm italic text-ink/70">
            {posting.softNotes}
          </p>
        ) : null}
      </div>

      <div className="rounded-2xl border border-smoke bg-paper p-5">
        <h3 className="mb-3 text-sm font-semibold text-ink">Tổng quan</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <InfoRow label="Trạng thái">
            {POSTING_STATE_LABEL_VI[posting.state as PostingState] ??
              posting.state}
          </InfoRow>
          <InfoRow label="Số lượng cần">{posting.headcount} người</InfoRow>
          <InfoRow label="Hình thức">
            {EMPLOYMENT_TYPE_LABEL_VI[
              posting.employmentType as EmploymentType
            ] ?? posting.employmentType}
          </InfoRow>
          <InfoRow label="Loại địa điểm">
            {VENUE_TYPE_LABEL_VI[posting.venueType as VenueType] ??
              posting.venueType}
          </InfoRow>
          <InfoRow label="Lương">
            {formatVnd(Number(posting.salaryMin))} —{" "}
            {formatVnd(Number(posting.salaryMax))} /{" "}
            {SALARY_TYPE_LABEL_VI[posting.salaryType as SalaryType] ??
              posting.salaryType}
          </InfoRow>
          <InfoRow label="Ngày tạo">
            {new Date(posting.createdAt).toLocaleDateString("vi-VN")}
          </InfoRow>
          {posting.publishedAt ? (
            <InfoRow label="Đăng">
              {new Date(posting.publishedAt).toLocaleDateString("vi-VN")}
            </InfoRow>
          ) : null}
          {posting.closedAt ? (
            <InfoRow label="Đóng">
              {new Date(posting.closedAt).toLocaleDateString("vi-VN")}
            </InfoRow>
          ) : null}
        </dl>
      </div>

      {posting.benefits.length > 0 ? (
        <div className="rounded-2xl border border-smoke bg-paper p-5">
          <h3 className="mb-2 text-sm font-semibold text-ink">Quyền lợi</h3>
          <div className="flex flex-wrap gap-1.5">
            {posting.benefits.map((b) => (
              <span
                key={b}
                className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800"
              >
                • {BENEFIT_LABEL_VI[b as keyof typeof BENEFIT_LABEL_VI] ?? b}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {posting.skillsRequired.length + posting.skillsPreferred.length > 0 ? (
        <div className="rounded-2xl border border-smoke bg-paper p-5">
          <h3 className="mb-2 text-sm font-semibold text-ink">Kỹ năng</h3>
          {posting.skillsRequired.length > 0 ? (
            <div className="mb-2">
              <p className="mb-1 text-xs text-ash">Bắt buộc</p>
              <div className="flex flex-wrap gap-1.5">
                {posting.skillsRequired.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-800"
                  >
                    {SKILL_LABEL_VI[s] ?? s}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {posting.skillsPreferred.length > 0 ? (
            <div>
              <p className="mb-1 text-xs text-ash">Ưu tiên</p>
              <div className="flex flex-wrap gap-1.5">
                {posting.skillsPreferred.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-ink/5 px-2 py-0.5 text-xs"
                  >
                    {SKILL_LABEL_VI[s] ?? s}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {showReqs ? (
        <div className="rounded-2xl border border-smoke bg-paper p-5">
          <h3 className="mb-2 text-sm font-semibold text-ink">
            Yêu cầu ứng viên
          </h3>
          <ul className="space-y-1 text-sm text-ink/80">
            {reqs.gender ? (
              <li>
                • Giới tính:{" "}
                {GENDER_LABEL_VI[reqs.gender as Gender] ?? reqs.gender}
              </li>
            ) : null}
            {reqs.minAge || reqs.maxAge ? (
              <li>
                • Tuổi: {reqs.minAge ?? "—"} – {reqs.maxAge ?? "—"}
              </li>
            ) : null}
            {reqs.minHeightCm ? (
              <li>• Cao tối thiểu: {reqs.minHeightCm}cm</li>
            ) : null}
            {reqs.motorbikeRequired ? <li>• Cần có xe máy</li> : null}
            {reqs.excludeVisibleTattoo ? (
              <li>• Không có hình xăm visible</li>
            ) : null}
            {reqs.kycCccdRequired ? <li>• Cần xác thực CCCD</li> : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function InfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-3 border-b border-smoke pb-2 last:border-b-0 last:pb-0 sm:block sm:border-b-0 sm:pb-0">
      <dt className="text-xs uppercase tracking-wide text-ash">{label}</dt>
      <dd className="text-right text-ink sm:mt-0.5 sm:text-left">{children}</dd>
    </div>
  );
}

function ApplicantsTab({
  postingId,
  orgId,
  postingActive,
}: {
  postingId: string;
  orgId: string;
  postingActive: boolean;
}) {
  const { data, isLoading } = useAdminApplicants(postingId);

  if (isLoading) return <Skeleton className="h-32 rounded-2xl" />;
  const items = data?.items ?? [];
  if (items.length === 0)
    return (
      <p className="rounded-2xl border border-dashed border-smoke bg-paper p-8 text-center text-sm text-ash">
        Chưa có ứng viên.
      </p>
    );

  return (
    <ul className="space-y-2">
      {!postingActive ? (
        <li className="rounded-2xl border-[1.5px] border-smoke bg-mist/40 px-4 py-3 text-sm text-ink">
          Tin tuyển dụng đã đóng — không thể mời phỏng vấn ứng viên mới. Chỉ xem
          được các ứng viên đã mời trước đó.
        </li>
      ) : null}
      {items.map((a) => (
        <li key={a.application.id}>
          <AdminApplicantRow
            applicant={a}
            postingId={postingId}
            orgId={orgId}
            postingActive={postingActive}
          />
        </li>
      ))}
    </ul>
  );
}

type MoreMenuItem = {
  label: string;
  onClick: () => void;
  destructive?: boolean;
};

function MoreMenu({ items }: { items: MoreMenuItem[] }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        ⋯
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 min-w-[160px] overflow-hidden rounded-xl border border-smoke bg-paper shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={`block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-mist ${
                item.destructive ? "text-danger" : "text-ink"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
